import type { estypes } from '@elastic/elasticsearch';

import {
  type AggregatedMatchupCentreResultBody,
  type MatchupCentreAggregations,
  type MatchupCentreESResult,
  type MatchupCentreESResultBody,
  type MatchupCentreMarket,
  type MatchupCentreResult,
} from '~/search-domain/pipelines/match-centre/types';
import { type Loggable } from '~/search-domain/types';
import { type BettingInsightsElasticSearchContext } from '~/search-domain/types/contracts';
import { replaceResponseHits } from '~/search-domain/utils';
import {
  extractMatchupCentreBet,
  extractMatchupCentreMarket,
  extractMatchupCentreSubevent,
  isMarketSupported,
  type MatchupCentreBet,
} from '~/search-domain/utils/response-factories';

export type UniqueBetsAggregation = {
  value: number;
};

export type OriginalAggregations = {
  subevents: SubeventsAggregation;
};

export type SubeventsAggregation = {
  buckets: SubeventsBucket[];
};

export type AggregationBucket = {
  key: number;
};

export type SubeventsBucket = AggregationBucket & {
  markets: { buckets: MarketsBucket[] };
};

export type MarketsBucket = AggregationBucket & {
  bets: { buckets: BetsBucket[] };
};

export type BetsBucket = AggregationBucket & {
  best_odds: {
    hits: {
      hits: estypes.SearchHit<MatchupCentreESResult>[];
    };
  };
};
export type { MatchupCentreESResultBody, AggregatedMatchupCentreResultBody };

type SubeventExtractor = (
  result: MatchupCentreESResult,
  userPreference: BettingInsightsElasticSearchContext['userPreference'],
) => MatchupCentreResult;

type AggregationState = {
  originalHits: Map<number, estypes.SearchHit<MatchupCentreESResult>>;
  subevents: Map<number, MatchupCentreResult>;
};

export async function createResponseFactory(
  this: Loggable,
  config: BettingInsightsElasticSearchContext,
  searchResponse: MatchupCentreESResultBody,
  from = 0,
  hitsPerPage = 10,
): Promise<AggregatedMatchupCentreResultBody> {
  return buildMatchupCentreResponse.call(this, config, searchResponse, from, hitsPerPage, extractMatchupCentreSubevent);
}

async function buildMatchupCentreResponse(
  this: Loggable,
  config: BettingInsightsElasticSearchContext,
  searchResponse: MatchupCentreESResultBody,
  from: number,
  hitsPerPage: number,
  extractSubevent: SubeventExtractor,
): Promise<AggregatedMatchupCentreResultBody> {
  const aggregations = searchResponse.aggregations as OriginalAggregations;
  const { subevents: subeventsGroup, ...baseAggregations } = aggregations;
  const { originalHits, subevents } = aggregateSubevents.call(this, subeventsGroup, from, hitsPerPage, {
    config,
    extractSubevent,
  });

  const hits = buildHits(subevents, originalHits, hitsPerPage);

  return replaceResponseHits(searchResponse, {
    aggregations: baseAggregations as MatchupCentreAggregations,
    hits,
    isWalled: config.isWalled,
    totalRelation: 'eq',
    totalValue: subeventsGroup.buckets.length,
  });
}

function aggregateSubevents(
  this: Loggable,
  subeventsGroup: SubeventsAggregation,
  from: number,
  hitsPerPage: number,
  params: {
    config: BettingInsightsElasticSearchContext;
    extractSubevent: SubeventExtractor;
  },
): AggregationState {
  const state: AggregationState = {
    subevents: new Map<number, MatchupCentreResult>(),
    originalHits: new Map<number, estypes.SearchHit<MatchupCentreESResult>>(),
  };

  // Process more subevents than requested because some are filtered out by market quality rules.
  const processCount = Math.min(subeventsGroup.buckets.length, from + hitsPerPage * 3);
  const relevantSubevents = subeventsGroup.buckets.slice(from, processCount);

  for (const { key: subeventId, markets } of relevantSubevents) {
    let subevent = state.subevents.get(subeventId);

    for (const { bets, key: marketId } of markets.buckets) {
      let market = subevent?.markets.find((item: MatchupCentreMarket) => item.marketId === marketId);

      for (const { best_odds } of bets.buckets) {
        for (const hit of best_odds.hits.hits) {
          const result = hit._source;
          if (!result) continue;

          subevent = subevent ?? createSubevent.call(this, result, hit, state, params.config, params.extractSubevent);
          if (!subevent) continue;

          market = market ?? createMarket.call(this, result, marketId, subevent, params.config);
          if (!market) continue;

          const bet = createBet.call(this, result);
          if (!bet) continue;
          upsertMarketBet(market, bet);
        }
      }

      if (subevent && market && shouldIncludeMarket(market)) {
        subevent.markets.push(market);
      }
    }
  }

  return state;
}

function createSubevent(
  this: Loggable,
  result: MatchupCentreESResult,
  hit: estypes.SearchHit<MatchupCentreESResult>,
  state: AggregationState,
  config: BettingInsightsElasticSearchContext,
  extractSubevent: SubeventExtractor,
): MatchupCentreResult | undefined {
  try {
    const subevent = extractSubevent(result, config.userPreference);
    subevent.markets = [];
    state.subevents.set(result.subeventId, subevent);
    state.originalHits.set(result.subeventId, hit);
    return subevent;
  } catch (error) {
    this.logger.warn(`Failed to parse subevent fields: ${result.subeventId}`, error);
    return undefined;
  }
}

function createMarket(
  this: Loggable,
  result: MatchupCentreESResult,
  marketId: number,
  subevent: MatchupCentreResult,
  config: BettingInsightsElasticSearchContext,
): MatchupCentreMarket | undefined {
  try {
    const market = extractMatchupCentreMarket(result);
    if (!isMarketSupported(market, config)) return undefined;
    subevent.betTypeId.push(market.betTypeId);
    market.bets = [];
    return market;
  } catch (error) {
    this.logger.warn(`Failed to parse market fields: ${marketId}`, error);
    return undefined;
  }
}

function createBet(this: Loggable, result: MatchupCentreESResult): MatchupCentreBet | undefined {
  try {
    return extractMatchupCentreBet(result);
  } catch (error) {
    this.logger.warn(
      `[Matchup Centre] Failed to parse bet - SubeventId: ${result.subeventId}, MarketId: ${result.marketId}, BetId: ${result.betId}`,
      {
        error: error instanceof Error ? error.message : String(error),
        betName: result.betName,
        bookmakerCode: result.bookmakerCode,
      },
    );
    return undefined;
  }
}

function upsertMarketBet(market: MatchupCentreMarket, bet: MatchupCentreBet): void {
  const marketBet = toMarketBet(bet);
  const existingBetIndex = market.bets.findIndex(existingBet => existingBet.betId === bet.betId);
  if (existingBetIndex === -1) {
    market.bets.push(marketBet);
    return;
  }
  market.bets[existingBetIndex] = marketBet;
}

function toMarketBet(bet: MatchupCentreBet): MatchupCentreMarket['bets'][number] {
  // Normalization boundary: parser returns some matchup fields as optional/null,
  // while market model keeps a strict bet shape for downstream UI consumption.
  return {
    ...bet,
    betCount: bet.betCount ?? 0,
    betPercentage: bet.betPercentage ?? 0,
    expectedValue: bet.expectedValue ?? 0,
    expectedValueStatus: bet.expectedValueStatus ?? '',
    stakeBetDiff: bet.stakeBetDiff ?? 0,
    stakePercentage: bet.stakePercentage ?? 0,
  };
}

function shouldIncludeMarket(market: MatchupCentreMarket): boolean {
  // Inclusion rules:
  // - 3-way (with Draw): require exactly 2 non-Draw bets
  // - 2-way markets: allow partial data (>=1 bet) to keep cards renderable
  // - Over/Under: allow partial data (>=1 bet)
  const nonDrawBets = market.bets.filter(bet => bet.betName !== 'Draw');
  const hasDrawOption = market.bets.some(bet => bet.betName === 'Draw');
  const isOverUnderMarket = market.marketClassification === 'OVER_UNDER';

  return (
    (hasDrawOption && nonDrawBets.length === 2) ||
    (!hasDrawOption && !isOverUnderMarket && market.bets.length >= 1) ||
    (!hasDrawOption && isOverUnderMarket && market.bets.length >= 1)
  );
}

function buildHits(
  subevents: Map<number, MatchupCentreResult>,
  originalHits: Map<number, estypes.SearchHit<MatchupCentreESResult>>,
  hitsPerPage: number,
) {
  return (
    Array.from(subevents.values())
      .filter(({ markets }) => markets.length > 0)
      // Limit to requested page size after filtering unsupported/invalid markets.
      .slice(0, hitsPerPage)
      .map(subevent => {
        const originalHit = originalHits.get(subevent.subeventId);
        return {
          ...(originalHit || {}),
          _index: originalHit?._index || '',
          _source: subevent,
        };
      })
  );
}
